/**
 * @file Chain and token registry for the 1auth SDK.
 *
 * Wraps `@rhinestone/shared-configs` chain/token data and combines it with
 * viem's chain definitions to expose a filtered, testnet-aware registry.
 * Consumers can look up supported chains and tokens, resolve token addresses
 * by symbol, and retrieve chain metadata such as explorer URLs and default RPC
 * endpoints.
 *
 * Testnet inclusion is controlled by the `includeTestnets` option on each
 * function or, as a project-wide default, by the environment variables
 * `NEXT_PUBLIC_ORCHESTRATOR_USE_TESTNETS` or `ORCHESTRATOR_USE_TESTNETS`.
 */

import type { Address, Chain } from "viem";
import { isAddress } from "viem";
import * as viemChains from "viem/chains";
import { chainRegistry } from "@rhinestone/shared-configs";

export type TokenConfig = {
  symbol: string;
  address: Address;
  decimals: number;
};

export type ChainFilterOptions = {
  includeTestnets?: boolean;
  chainIds?: number[];
};

type ChainRegistryEntry = (typeof chainRegistry)[keyof typeof chainRegistry];

const env: Record<string, string | undefined> =
  typeof process !== "undefined" ? process.env : {};

// Build a chain-ID-to-Chain map from viem, resolving collisions by preferring
// mainnet definitions over testnets. viem exports duplicate numeric IDs (e.g.
// zoraTestnet and hyperEvm both use 999); a naive Map would silently keep
// whichever entry happens to appear last in iteration order, which may be the
// testnet. The loop below only overwrites an existing entry when the incoming
// chain is NOT a testnet and the existing one IS, ensuring the production chain
// always wins while still falling back to a testnet entry when no mainnet
// definition exists for a given ID.
const VIEM_CHAIN_BY_ID = new Map<number, Chain>();
for (const value of Object.values(viemChains)) {
  if (typeof value !== "object" || value === null || !("id" in value) || !("name" in value)) continue;
  const chain = value as Chain;
  const existing = VIEM_CHAIN_BY_ID.get(chain.id);
  if (!existing || (existing.testnet && !chain.testnet)) {
    VIEM_CHAIN_BY_ID.set(chain.id, chain);
  }
}

function isEvmRegistryEntry(
  entry: ChainRegistryEntry | undefined,
): entry is ChainRegistryEntry & { vmType: "evm" } {
  return entry?.vmType === "evm";
}

function getEvmRegistryEntry(
  chainId: number,
): (ChainRegistryEntry & { vmType: "evm" }) | undefined {
  const entry = chainRegistry[String(chainId) as keyof typeof chainRegistry];
  return isEvmRegistryEntry(entry) ? entry : undefined;
}

// The shared-configs registry includes non-EVM chains (Solana, Tron). Filter
// to EVM entries before exposing supported chains — downstream consumers
// (viem clients, wallet_getCapabilities, RPC proxy) only handle eip155 chains.
const REGISTRY_CHAIN_IDS = Object.entries(chainRegistry)
  .filter(([, entry]) => isEvmRegistryEntry(entry))
  .map(([id]) => Number(id));
const SUPPORTED_CHAIN_IDS = new Set(REGISTRY_CHAIN_IDS);

function tokensForChain(chainId: number): TokenConfig[] {
  const entry = getEvmRegistryEntry(chainId);
  if (!entry) return [];
  return entry.tokens.map((token) => ({
    symbol: token.symbol,
    address: token.address as Address,
    decimals: token.decimals,
  }));
}

/** Treat any 20-byte hex string as an address; checksum casing is optional for user inputs. */
function isAddressInput(value: string): value is Address {
  return isAddress(value, { strict: false });
}

/**
 * Parse a string environment variable into a boolean, returning `undefined`
 * when the value is absent or not a recognised truthy/falsy literal.
 *
 * Accepted truthy values: `"true"`, `"1"`.
 * Accepted falsy values: `"false"`, `"0"`.
 */
function parseBool(value?: string): boolean | undefined {
  if (value === "true" || value === "1") return true;
  if (value === "false" || value === "0") return false;
  return undefined;
}

/**
 * Determine whether testnet chains should be included in registry results.
 *
 * Precedence (highest to lowest):
 * 1. `explicit` argument — a caller-supplied boolean always wins.
 * 2. `NEXT_PUBLIC_ORCHESTRATOR_USE_TESTNETS` env var — checked first so
 *    Next.js browser bundles (which only expose `NEXT_PUBLIC_*` variables)
 *    can configure testnet mode without a server-side env var.
 * 3. `ORCHESTRATOR_USE_TESTNETS` env var — server-side / Node.js fallback.
 * 4. Defaults to `false` (testnets excluded) when none of the above is set.
 */
function resolveIncludeTestnets(explicit?: boolean): boolean {
  if (explicit !== undefined) return explicit;
  const envValue =
    parseBool(env.NEXT_PUBLIC_ORCHESTRATOR_USE_TESTNETS) ??
    parseBool(env.ORCHESTRATOR_USE_TESTNETS);
  return envValue ?? false;
}

function applyChainFilters(chainIds: number[], options?: ChainFilterOptions): number[] {
  const includeTestnets = resolveIncludeTestnets(options?.includeTestnets);
  const allowlist = options?.chainIds;
  let filtered = chainIds;

  if (!includeTestnets) {
    filtered = filtered.filter((chainId) => !isTestnet(chainId));
  }

  if (allowlist) {
    const allowed = new Set(allowlist);
    filtered = filtered.filter((chainId) => allowed.has(chainId));
  }

  return filtered;
}

export function getSupportedChainIds(options?: ChainFilterOptions): number[] {
  return applyChainFilters(REGISTRY_CHAIN_IDS, options);
}

export function getSupportedChains(options?: ChainFilterOptions): Chain[] {
  return getSupportedChainIds(options)
    .map((chainId) => VIEM_CHAIN_BY_ID.get(chainId))
    .filter((chain): chain is Chain => Boolean(chain));
}

export function getAllSupportedChainsAndTokens(options?: ChainFilterOptions): Array<{
  chainId: number;
  tokens: TokenConfig[];
}> {
  return getSupportedChainIds(options).map((chainId) => ({
    chainId,
    tokens: tokensForChain(chainId),
  }));
}

export function getChainById(chainId: number): Chain {
  if (!SUPPORTED_CHAIN_IDS.has(chainId)) {
    throw new Error(`Unsupported chain ID: ${chainId}`);
  }
  const chain = VIEM_CHAIN_BY_ID.get(chainId);
  if (!chain) {
    throw new Error(`Unsupported chain ID: ${chainId}`);
  }
  return chain;
}

export function getChainName(chainId: number): string {
  try {
    return getChainById(chainId).name;
  } catch {
    return `Chain ${chainId}`;
  }
}

export function getChainExplorerUrl(chainId: number): string | undefined {
  try {
    return getChainById(chainId).blockExplorers?.default?.url;
  } catch {
    return undefined;
  }
}

export function getChainRpcUrl(chainId: number): string | undefined {
  try {
    const chain = getChainById(chainId);
    return chain.rpcUrls?.default?.http?.[0] || chain.rpcUrls?.public?.http?.[0];
  } catch {
    return undefined;
  }
}

export function getSupportedTokens(chainId: number): TokenConfig[] {
  return tokensForChain(chainId);
}

export function getSupportedTokenSymbols(chainId: number): string[] {
  return getSupportedTokens(chainId).map((token) => token.symbol);
}

export function getTokenAddress(symbolOrAddress: string, chainId: number): Address {
  if (isAddressInput(symbolOrAddress)) {
    return symbolOrAddress;
  }
  const match = getSupportedTokens(chainId).find(
    (t) => t.symbol.toUpperCase() === symbolOrAddress.toUpperCase()
  );
  if (!match) {
    throw new Error(`Unsupported token "${symbolOrAddress}" on chain ${chainId}`);
  }
  return match.address;
}

export function getTokenDecimals(symbolOrAddress: string, chainId: number): number {
  const match = getSupportedTokens(chainId).find(
    (t) =>
      isAddressInput(symbolOrAddress)
        ? t.address.toLowerCase() === symbolOrAddress.toLowerCase()
        : t.symbol.toUpperCase() === symbolOrAddress.toUpperCase()
  );
  if (!match && isAddressInput(symbolOrAddress) && SUPPORTED_CHAIN_IDS.has(chainId)) {
    return 18;
  }
  if (!match) {
    throw new Error(`Unsupported token "${symbolOrAddress}" on chain ${chainId}`);
  }
  return match.decimals;
}

export function resolveTokenAddress(token: string, chainId: number): Address {
  if (isAddressInput(token)) {
    return token;
  }
  return getTokenAddress(token, chainId);
}

export function isTestnet(chainId: number): boolean {
  try {
    return getChainById(chainId).testnet ?? false;
  } catch {
    return false;
  }
}

export function getTokenSymbol(tokenAddress: Address, chainId: number): string {
  const token = getSupportedTokens(chainId).find(
    (entry) => entry.address.toLowerCase() === tokenAddress.toLowerCase()
  );
  if (token) {
    return token.symbol;
  }
  if (!SUPPORTED_CHAIN_IDS.has(chainId)) {
    throw new Error(`Unsupported chain ID: ${chainId}`);
  }
  return "Token";
}

export function isTokenAddressSupported(tokenAddress: Address, chainId: number): boolean {
  return isAddressInput(tokenAddress) && SUPPORTED_CHAIN_IDS.has(chainId);
}
